"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Calendar as CalendarIcon, Loader, Minus, Plus } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
const inclusions = ["PRECISION DIE-CUTTING", "HIGH-SPEED SLITTING", "MULTI-LAYER LAMINATION", "TECHNICAL CONSULTATION"];
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [dateOpen, setDateOpen] = useState(false);
  if (isSubmitted && !submitError) {
    return <div className="bg-white border border-slate-200 p-10 md:p-14 text-center">
        <p className="text-[11px] tracking-[0.3em] uppercase font-default text-blue-600 mb-6">
          GST_REF ◦ RECEIVED
        </p>
        <h3 className="font-default text-3xl md:text-4xl font-bold text-slate-900 mb-6">
          Request Successfully Submitted
        </h3>
        <p className="font-default text-slate-600 mb-8 max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} className="rounded-md px-8 h-12 bg-blue-600 text-white hover:bg-blue-700">
          Submit Another Request
        </Button>
      </div>;
  }
  return <div className="bg-white border border-slate-200 p-8 md:p-14">
      <Form onSubmit={handleSubmit} className="space-y-7">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Please enter your full name"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.25em] uppercase font-default text-slate-500">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="border-0 border-b border-slate-300 px-0 h-12 bg-transparent focus-visible:ring-0 focus-visible:border-blue-600 focus-visible:rounded-md focus-visible:border focus-visible:px-4 transition-all text-slate-900 font-default text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.25em] uppercase font-default text-slate-500">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@company.com" className="border-0 border-b border-slate-300 px-0 h-12 bg-transparent focus-visible:ring-0 focus-visible:border-blue-600 focus-visible:rounded-md focus-visible:border focus-visible:px-4 transition-all text-slate-900 font-default text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[\d\s\-()]{7,}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.25em] uppercase font-default text-slate-500">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="border-0 border-b border-slate-300 px-0 h-12 bg-transparent focus-visible:ring-0 focus-visible:border-blue-600 focus-visible:rounded-md focus-visible:border focus-visible:px-4 transition-all text-slate-900 font-default text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_date" rules={{
        required: "Please select a date"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.25em] uppercase font-default text-slate-500">
                Target Date
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input readOnly onClick={() => setDateOpen(v => !v)} placeholder="Select a date" value={field.value ? new Date(field.value).toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric"
            }) : ""} className="border-0 border-b border-slate-300 px-0 h-12 bg-transparent focus-visible:ring-0 focus-visible:border-blue-600 focus-visible:rounded-md focus-visible:border focus-visible:px-4 transition-all text-slate-900 font-default text-base cursor-pointer pr-10" />
                  <SafeHydrate><CalendarIcon className="absolute right-0 top-1/2 -translate-y-1/2 size-4 text-slate-400 pointer-events-none" /></SafeHydrate>
                  {dateOpen && <div className="absolute z-20 mt-2 bg-white border border-slate-200 shadow-lg p-2">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                  field.onChange(date ? date.toISOString() : "");
                  setDateOpen(false);
                }} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} /></SafeHydrate>
                    </div>}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_type" rules={{
        required: "Please choose a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.25em] uppercase font-default text-slate-500">
                Service Required
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full border-0 border-b border-slate-300 px-0 h-12 bg-transparent focus-visible:ring-0 focus-visible:border-blue-600 text-slate-900 font-default text-base">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md">
                    <SelectItem value="die_cutting">Precision Die-Cutting</SelectItem>
                    <SelectItem value="slitting">Lathe Slitting</SelectItem>
                    <SelectItem value="laminating">Advanced Lamination</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="quantity" rules={{
        required: "Please indicate quantity",
        min: {
          value: 1,
          message: "Minimum 1 unit"
        }
      }} render={({
        field
      }) => {
        const value = Number(field.value) || 1;
        return <FormItem>
                <FormLabel className="text-[11px] tracking-[0.25em] uppercase font-default text-slate-500">
                  Estimated Quantity
                </FormLabel>
                <FormControl>
                  <div className="flex items-center justify-between border-b border-slate-300 pb-2">
                    <span className="font-default text-slate-900 text-base">
                      {value} {value === 1 ? "unit" : "units"}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="icon" className="rounded-md size-9 border-slate-300" onClick={() => field.onChange(Math.max(1, value - 1))} disabled={value <= 1}>
                        <Minus className="size-3.5" />
                      </Button>
                      <Button type="button" variant="outline" size="icon" className="rounded-md size-9 border-slate-300" onClick={() => field.onChange(value + 1)}>
                        <Plus className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>;
      }} />

        <FormField control={control} name="project_details" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.25em] uppercase font-default text-slate-500">
                Project Specifications
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Describe your material, dimensions, and application requirements..." rows={4} className="border-0 border-b border-slate-300 px-0 bg-transparent focus-visible:ring-0 focus-visible:border-blue-600 focus-visible:rounded-md focus-visible:border focus-visible:px-4 focus-visible:py-3 transition-all text-slate-900 font-default text-base resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm font-default">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full rounded-md h-14 bg-blue-600 hover:bg-blue-700 text-white text-[11px] tracking-[0.3em] uppercase font-default">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : "Request Quote"}
        </Button>

        <p className="text-[11px] tracking-[0.25em] uppercase font-default text-slate-500 text-center pt-2">
          ENGINEERING RESPONSE WITHIN 24 HOURS ◦ PRECISION MANUFACTURING
        </p>
      </Form>
    </div>;
}
export default function QuoteRequestFormRequest() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3806" id="quote-request-form" className="bg-[#F8F5F8] text-slate-900 py-24 md:py-44 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div ref={leftRef} className={`lg:col-start-1 lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] tracking-[0.35em] uppercase font-default text-blue-600 mb-10">
              GST_REF ◦ QUOTE REQUEST
            </p>

            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-slate-900 mb-10">
              Precision Converting
              <br />
              Solutions for Excellence.
            </h2>

            <p className="font-default text-lg md:text-xl leading-relaxed text-slate-600 mb-12 max-w-md">
              Global Standard Technology delivers engineered die-cutting, slitting, and laminating services. 
              Submit your project specifications below to receive a technical assessment and production quote.
            </p>

            <div className="max-w-md">
              <p className="text-[11px] tracking-[0.3em] uppercase font-default text-blue-600 mb-6">
                Core Capabilities
              </p>
              <ul className="border-t border-slate-200">
                {inclusions.map((item, i) => <li key={i} data-index={i} className="border-b border-slate-200 py-4 text-[11px] tracking-[0.25em] uppercase font-default text-slate-700">
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-start-7 lg:col-span-6 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="quote_request_form_contact" formId="quote_request_form_contact" sectionName="quote-request-form" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            project_date: "",
            service_type: "",
            quantity: 1,
            project_details: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true,
              pattern: "phone"
            },
            project_date: {
              required: true
            },
            service_type: {
              required: true
            },
            quantity: {
              required: true,
              min: 1
            }
          }} submitText="Request Quote" successMessage="Your request has been received. Our engineering team will review your specifications and contact you within 24 hours." errorMessage="Submission failed. Please verify your details or contact our sales team directly.">
              <BookingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}